import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';
import { AnimateOnScroll, CountUpNumber } from './AnimateOnScroll';

const StatsSection = () => {
  const { t, tm } = useLanguage();
  const itemCopy = tm('home.stats.items');

  const stats = useMemo(
    () => [
      {
        id: 1,
        icon: 'Briefcase',
        value: '880+',
        label: itemCopy[0]?.label || 'Projects Completed',
        description: itemCopy[0]?.description || 'High-precision automation & consulting deliveries',
        color: 'text-amber-400',
        bgColor: 'bg-amber-500/10 border-amber-500/20',
      },
      {
        id: 2,
        icon: 'Building2',
        value: '678+',
        label: itemCopy[1]?.label || 'Organizations Served',
        description: itemCopy[1]?.description || 'Enterprise partners and MNC clients globally',
        color: 'text-amber-400',
        bgColor: 'bg-amber-500/10 border-amber-500/20',
      },
      {
        id: 3,
        icon: 'Award',
        value: '112',
        label: itemCopy[2]?.label || 'Excellence Awards',
        description: itemCopy[2]?.description || 'Industry recognitions for technical leadership',
        color: 'text-amber-400',
        bgColor: 'bg-amber-500/10 border-amber-500/20',
      },
      {
        id: 4,
        icon: 'Users',
        value: '15K+',
        label: itemCopy[3]?.label || 'Talent Placements',
        description: itemCopy[3]?.description || 'Vetted technical experts deployed across industries',
        color: 'text-amber-400',
        bgColor: 'bg-amber-500/10 border-amber-500/20',
      },
    ],
    [itemCopy]
  );

  return (
    <section className="bg-[#060D21] text-white py-16 md:py-24 relative overflow-hidden border-y border-slate-800">
      {/* Background Glows */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-amber-500/20 rounded-full blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-600/20 rounded-full blur-[140px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <AnimateOnScroll>
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full mb-3">
              <Icon name="TrendingUp" size={15} className="text-amber-400" />
              <span className="text-xs md:text-sm font-bold tracking-widest text-amber-400 uppercase">
                {t('home.stats.badge') || 'PROVEN TRACK RECORD'}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-5xl font-extrabold text-white tracking-tight mb-3">
              {t('home.stats.title') || 'Measurable Enterprise Impact'}
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-slate-300 max-w-2xl mx-auto font-normal">
              {t('home.stats.subtitle') || 'Delivering scalable technology solutions and workforce excellence.'}
            </p>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {stats.map((stat, index) => (
            <AnimateOnScroll key={stat.id} delay={index * 100}>
              <div className="bg-slate-900/70 backdrop-blur-xl rounded-2xl p-6 md:p-8 border border-slate-800 hover:border-amber-500/40 transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between h-full group shadow-xl">
                <div>
                  <div
                    className={`w-12 h-12 md:w-14 md:h-14 rounded-xl ${stat.bgColor} border flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-300`}
                  >
                    <Icon name={stat.icon} size={24} className={stat.color} />
                  </div>
                  <div className="text-3xl md:text-4xl lg:text-5xl font-black text-amber-400 mb-2 tracking-tight">
                    <CountUpNumber targetNumber={stat.value} />
                  </div>
                  <div className="text-base md:text-lg font-bold text-white mb-2">
                    {stat.label}
                  </div>
                </div>
                <div className="text-xs md:text-sm text-slate-300 leading-relaxed pt-2">
                  {stat.description}
                </div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>

        <AnimateOnScroll delay={400}>
          <div className="mt-12 md:mt-16 bg-slate-900/80 backdrop-blur-xl rounded-2xl p-8 md:p-12 border border-slate-800 shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-slate-800">
              <div className="pt-4 md:pt-0">
                <div className="text-3xl md:text-4xl lg:text-5xl font-black text-amber-400 mb-2">
                  <CountUpNumber targetNumber="98.5%" />
                </div>
                <div className="text-sm md:text-base text-slate-300 font-medium">
                  {t('home.stats.satisfaction') || 'Client Satisfaction Rating'}
                </div>
              </div>
              <div className="pt-6 md:pt-0">
                <div className="text-3xl md:text-4xl lg:text-5xl font-black text-amber-400 mb-2">
                  24/7
                </div>
                <div className="text-sm md:text-base text-slate-300 font-medium">
                  {t('home.stats.support') || 'Global Support & SLA Coverage'}
                </div>
              </div>
              <div className="pt-6 md:pt-0">
                <div className="text-3xl md:text-4xl lg:text-5xl font-black text-amber-400 mb-2">
                  &lt;48hrs
                </div>
                <div className="text-sm md:text-base text-slate-300 font-medium">
                  {t('home.stats.response') || 'Average Deployment Turnaround'}
                </div>
              </div>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default StatsSection;
